import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { FileText, ArrowLeft } from 'lucide-react';
import { useTranslations } from '../../hooks/useTranslations';
import { Header } from '../Header';
import { Footer } from '../Footer';
import { translations } from '../../locales';
import { getLocalizedPath, type LocalizedBasePath } from '../../utils/localizedRoutes';

export function TermsOfUsePage() {
  const navigate = useNavigate();
  const { t, currentLanguage } = useTranslations();
  
  const buildUrl = (path: LocalizedBasePath) => {
    return getLocalizedPath(path, currentLanguage);
  };

  // Get terms translations, fallback to English if not available
  const termsData = (t as any).pages?.terms;
  const englishTerms = (translations.en as any).pages?.terms;
  const p = termsData || englishTerms;
  
  // Debug: log if translations are missing
  if (!termsData && import.meta.env.DEV) {
    console.log('Terms translations not found for language:', currentLanguage);
    console.log('Available keys in t:', Object.keys(t));
    console.log('Available pages:', (t as any).pages ? Object.keys((t as any).pages) : 'none');
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-purple-600 via-purple-700 to-blue-800" style={{ paddingTop: '140px' }}>
        <div className="container mx-auto px-4 py-12 max-w-4xl">
          {/* Back Button */}
          <button
            onClick={() => navigate(buildUrl('/'))}
            className="flex items-center text-white hover:text-purple-200 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            <span>Back to Home</span>
          </button>

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-16 h-16 bg-white/20 rounded-lg flex items-center justify-center">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-white text-4xl">{p?.title || 'Terms of Use'}</h1>
                <p className="text-purple-100">{p?.lastUpdated || 'Last updated: December 12, 2025'}</p>
              </div>
            </div>
          </div>

          <Card className="bg-white/95 backdrop-blur-sm shadow-xl">
            <CardContent className="p-8 space-y-8">
              <section>
                <h2 className="text-gray-900 text-2xl mb-4">{p?.definitions || '1. Definitions'}</h2>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  {(p?.definitionsItems || []).map((item: any, index: number) => (
                    <li key={index} className="mb-2">
                      <strong>{item?.term || ''}:</strong> {item?.definition || ''}
                    </li>
                  ))}
                </ul>
              </section>

              <section>
                <h2 className="text-gray-900 text-2xl mb-4">{p?.useOfService || '2. Use of the Service'}</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  {p?.useOfService21 || ''}
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  {p?.useOfService22 || ''}
                </p>
                <p className="text-gray-700 leading-relaxed">
                  {p?.useOfService23 || ''}
                </p>
              </section>

              <section>
                <h2 className="text-gray-900 text-2xl mb-4">{p?.accountRegistration || '3. Account Registration'}</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  {p?.accountRegistration31 || ''}
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  {p?.accountRegistration32 || ''}
                </p>
                <p className="text-gray-700 leading-relaxed">
                  {p?.accountRegistration33 || ''}
                </p>
              </section>

              <section>
                <h2 className="text-gray-900 text-2xl mb-4">{p?.paymentAndSubscriptions || '4. Payment and Subscriptions'}</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  {p?.paymentAndSubscriptions41 || ''}
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  {p?.paymentAndSubscriptions42 || ''}
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  {p?.paymentAndSubscriptions43 || ''}
                </p>
                <p className="text-gray-700 leading-relaxed">
                  {p?.paymentAndSubscriptions44 || ''}
                </p>
              </section>

              <section>
                <h2 className="text-gray-900 text-2xl mb-4">{p?.intellectualPropertyRights || '5. Intellectual Property Rights'}</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  {p?.intellectualPropertyRights51 || ''}
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  {p?.intellectualPropertyRights52 || ''}
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 mb-3 ml-4">
                  {(p?.intellectualPropertyRights52Items || []).map((item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
                <p className="text-gray-700 leading-relaxed mb-3">
                  {p?.intellectualPropertyRights53 || ''}
                </p>
                <p className="text-gray-700 leading-relaxed">
                  {p?.intellectualPropertyRights54 || ''}
                </p>
              </section>

              <section>
                <h2 className="text-gray-900 text-2xl mb-4">{p?.userContent || '6. User Content'}</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  {p?.userContent61 || ''}
                </p>
                <p className="text-gray-700 leading-relaxed">
                  {p?.userContent62 || ''}
                </p>
              </section>

              <section>
                <h2 className="text-gray-900 text-2xl mb-4">{p?.privacy || '7. Privacy'}</h2>
                <p className="text-gray-700 leading-relaxed">
                  {p?.privacyDesc || ''}
                </p>
              </section>

              <section>
                <h2 className="text-gray-900 text-2xl mb-4">{p?.termination || '8. Termination'}</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  {p?.termination81 || ''}
                </p>
                <p className="text-gray-700 leading-relaxed">
                  {p?.termination82 || ''}
                </p>
              </section>

              <section>
                <h2 className="text-gray-900 text-2xl mb-4">{p?.limitationOfLiability || '9. Limitation of Liability'}</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  {p?.limitationOfLiability91 || ''}
                </p>
                <p className="text-gray-700 leading-relaxed">
                  {p?.limitationOfLiability92 || ''}
                </p>
              </section>

              <section>
                <h2 className="text-gray-900 text-2xl mb-4">{p?.indemnification || '10. Indemnification'}</h2>
                <p className="text-gray-700 leading-relaxed">
                  {p?.indemnificationDesc || ''}
                </p>
              </section>

              <section>
                <h2 className="text-gray-900 text-2xl mb-4">{p?.governingLaw || '11. Governing Law'}</h2>
                <p className="text-gray-700 leading-relaxed">
                  {p?.governingLawDesc || ''}
                </p>
              </section>

              <section>
                <h2 className="text-gray-900 text-2xl mb-4">{p?.changesToTerms || '12. Changes to Terms'}</h2>
                <p className="text-gray-700 leading-relaxed">
                  {p?.changesToTermsDesc || ''}
                </p>
              </section>

              <section>
                <h2 className="text-gray-900 text-2xl mb-4">{p?.contactInformation || '13. Contact Information'}</h2>
                <p className="text-gray-700 leading-relaxed mb-2">
                  {p?.contactInformationDesc || ''}
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700">
                    <strong>{p?.companyName || ''}</strong><br />
                    {p?.companyAddress || ''}<br />
                    {p?.contactEmail || ''}
                  </p>
                </div>
              </section>
            </CardContent>
          </Card>
        </div>
        <Footer onShowBlog={() => navigate(buildUrl('/blog'))} />
      </div>
    </>
  );
}
